import type { ReactNode } from 'react';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@ophelos/ui/components/accordion';
import { CurrencyDisplay } from '@/components/ui/CurrencyDisplay';
import { useTranslation } from '@/hooks/useTranslation';

interface TotalBalanceCardProps {
  amount: number;
  currency: string;
  /** Shown under the label, e.g. how many debts the balance spans. */
  detail?: ReactNode;
  /** Rows that fold out under the balance, e.g. one per debt; the card becomes the trigger. */
  breakdown?: ReactNode;
  className?: string;
}

/** The balance a payment or plan goes against, shown at the top of every amount tab. */
export function TotalBalanceCard({ amount, currency, detail, breakdown, className = '' }: TotalBalanceCardProps) {
  const { t } = useTranslation();
  const box = `rounded-lg border bg-soft ${className}`;
  const row = (
    <>
      <span className="stack text-sm text-muted-foreground">
        {t('paymentTabs.totalBalance')}
        {detail && <span className="text-xs">{detail}</span>}
      </span>
      <CurrencyDisplay amount={amount} currency={currency} className="text-xl font-bold" />
    </>
  );

  if (!breakdown) return <section className={`${box} px-5 py-3 flex items-center justify-between gap-4`}>{row}</section>;

  return (
    <Accordion type="single" collapsible className={box}>
      <AccordionItem value="breakdown" className="border-b-0">
        <AccordionTrigger className="px-5 py-3 items-center hover:bg-transparent [&>span:first-child]:flex-1">{row}</AccordionTrigger>
        <AccordionContent className="px-5 pb-3 divide-y divide-border">{breakdown}</AccordionContent>
      </AccordionItem>
    </Accordion>
  );
}
